import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Call } from "@entities/call";
import { deviceOf, useCameraPreviewOn } from "@shared/media";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";

/**
 * Кнопка камеры с предпросмотром — как в Discord. Предпросмотр включён в
 * настройках и камера выключена — сперва окно с собой, и только по «Включить»
 * картинка уходит в канал. Выключается камера всегда сразу.
 */
export function useCameraSwitch(call: Call): { toggle: () => void; preview: ReactNode } {
  const wanted = useCameraPreviewOn((state) => state.on);
  const [open, setOpen] = useState(false);

  const toggle = () => {
    if (!call.camera && wanted) setOpen(true);
    else void call.toggleCamera();
  };

  const preview = open ? (
    <CameraPreview
      onClose={() => setOpen(false)}
      onConfirm={() => {
        setOpen(false);
        void call.toggleCamera();
      }}
    />
  ) : null;

  return { toggle, preview };
}

function CameraPreview({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let gone = false;

    void navigator.mediaDevices
      .getUserMedia({ video: deviceOf("camera") })
      .then((got) => {
        if (gone) {
          for (const track of got.getTracks()) track.stop();
          return;
        }
        stream = got;
        if (video.current) video.current.srcObject = got;
      })
      .catch((error: unknown) => setProblem(error instanceof Error ? error.message : "Камера недоступна"));

    // Окно закрыли — камера гаснет: в канал уйдёт уже своя дорожка звонка.
    return () => {
      gone = true;
      for (const track of stream?.getTracks() ?? []) track.stop();
    };
  }, []);

  return (
    <Dialog
      title="Так вас увидят"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={!!problem} onClick={onConfirm}>
            Включить камеру
          </Button>
        </>
      }
    >
      {problem ? (
        <div className="danger-text small">{problem}</div>
      ) : (
        <video ref={video} className="av-cam-view" autoPlay playsInline muted />
      )}
    </Dialog>
  );
}
